import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, setAccessToken, setRefreshToken, clearTokens, getRefreshToken } from './api';
import { ROLE_LEVELS } from './roles';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const res = await authAPI.me();
      if (res.ok) {
        const data = await res.clone().json();
        setUser(data);
        return true;
      }
    } catch {}
    return false;
  }, []);

  // On mount: try same-origin SSO probe via httpOnly cookie.
  // If a valid `tw1_id_session` cookie exists the backend returns fresh
  // tokens + user — we hydrate the in-memory state so the user appears
  // already-logged-in. On 401 (no/expired cookie) we silently stay logged out.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authAPI.session();
        if (!cancelled && res.ok) {
          const data = await res.clone().json();
          if (data?.access_token && data?.refresh_token && data?.user) {
            setAccessToken(data.access_token);
            setRefreshToken(data.refresh_token);
            setUser(data.user);
          }
        }
      } catch { /* tolerate — show form */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // Same as the on-mount probe but exposed for components (e.g. LoginPage)
  // that want to wait for the result and decide what to do with the tokens
  // (SSO redirect vs dashboard navigation).
  const restoreSession = useCallback(async () => {
    try {
      const res = await authAPI.session();
      if (res.ok) {
        const data = await res.clone().json();
        if (data?.access_token && data?.refresh_token && data?.user) {
          setAccessToken(data.access_token);
          setRefreshToken(data.refresh_token);
          setUser(data.user);
          return data;
        }
      }
    } catch { /* ignore */ }
    return null;
  }, []);

  const login = async (email, password) => {
    try {
      const res = await authAPI.login(email, password);
      const cloned = res.clone();
      const data = await cloned.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Inloggen mislukt');
      }
      
      setAccessToken(data.access_token);
      setRefreshToken(data.refresh_token);
      setUser(data.user);
      return data;
    } catch (error) {
      // Suppress platform injection clone errors, throw actual API errors
      if (error.message && error.message.includes("Response body is already used")) {
        throw new Error('Inloggen mislukt');
      }
      throw error;
    }
  };

  const logout = () => {
    // Clear in-memory tokens + user state immediately so the UI doesn't
    // briefly show authenticated content while the redirect is in flight.
    clearTokens();
    setUser(null);
    // Top-level GET to the IDP's own logout redirect endpoint. The browser
    // includes the SameSite=Lax `tw1_id_session` cookie (would NOT be sent
    // on a fetch/XHR POST due to credentials being intentionally restricted),
    // so the server can actually clear it via Set-Cookie. Same-origin
    // returnTo is auto-allowed by the validation rules in
    // GET /api/auth/logout, so this also covers the user clicking logout
    // while already on /login.
    window.location.href =
      `/api/auth/logout?returnTo=${encodeURIComponent('/login?logout=1')}`;
  };

  const hasMinRole = (minRole) => {
    if (!user) return false;
    const userLevel = ROLE_LEVELS[user.globale_rol] || 0;
    const minLevel = ROLE_LEVELS[minRole] || 0;
    return userLevel >= minLevel;
  };

  const value = {
    user,
    loading,
    login,
    logout,
    fetchMe,
    restoreSession,
    hasMinRole,
    isAdmin: hasMinRole('admin'),
    isOwner: hasMinRole('owner'),
    isSuperOwner: hasMinRole('super_owner'),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
